// 计费单位换算 helper —— 从 register.jsx 抽出为共享模块，供 register.jsx 与
// openapi-merge.js 共用（避免二者相互 import 造成循环依赖）。
//
// 约定：wire/后端 tokenPricing 用 per-token（每 token 单价，很小的小数）；
//       表单/UI 的 modelPricing 用 per-1M（每百万 token 单价，便于人读）。
// record(per-token) ↔ array(per-1M) 双向换算即 ×/÷ MILLION。

export const MILLION = 1_000_000;

// 所有按 token 计价的字段必须一起换算。toolCallPricing 是按次计价，原样保留。
export const TOKEN_PRICE_FIELDS = [
  'inputPricePerToken',
  'outputPricePerToken',
  'cacheCreationPricePerToken',
  'cacheCreation5mPricePerToken',
  'cacheCreation1hPricePerToken',
  'cacheReadPricePerToken',
  'imagePricePerToken',
  'audioInputPricePerToken',
  'audioOutputPricePerToken',
  'reasoningPricePerToken',
];

// 去掉二进制浮点换算噪声，同时保留足够的计费精度。
export const stablePriceNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Number(n.toPrecision(15)) : n;
};

const convertTokenFields = (source, factor, requiredDefaults = false) => {
  const out = {};
  for (const field of TOKEN_PRICE_FIELDS) {
    const value = source?.[field];
    if (value != null) out[field] = stablePriceNumber(Number(value) * factor);
    else if (requiredDefaults && (field === 'inputPricePerToken' || field === 'outputPricePerToken')) out[field] = 0;
  }
  return out;
};

const cloneToolCallPricing = (value) =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? { ...value }
    : undefined;

// record(per-token) → array(per-1M)：后端/导入形状 → 表单形状
export const pricingRecordToArray = (pricing) =>
  Object.entries(pricing || {}).map(([modelName, p]) => ({
    modelName,
    ...convertTokenFields(p, MILLION, true),
    ...(cloneToolCallPricing(p.toolCallPricing) ? { toolCallPricing: cloneToolCallPricing(p.toolCallPricing) } : {}),
    tiers: Array.isArray(p.tiers)
      ? p.tiers.map((t) => ({
          inputTokenThreshold: t.inputTokenThreshold ?? 0,
          ...convertTokenFields(t, MILLION, true),
        }))
      : undefined,
  }));

// array(per-1M) → record(per-token)：表单形状 → 后端 wire 形状
export const pricingArrayToRecord = (rows) => {
  const out = {};
  for (const r of rows || []) {
    const name = (r.modelName || '').trim() || 'unnamed';
    const entry = convertTokenFields(r, 1 / MILLION, true);
    const toolCallPricing = cloneToolCallPricing(r.toolCallPricing);
    if (toolCallPricing) entry.toolCallPricing = toolCallPricing;
    if (Array.isArray(r.tiers) && r.tiers.length > 0) {
      entry.tiers = r.tiers.map((t) => {
        return {
          inputTokenThreshold: t.inputTokenThreshold,
          ...convertTokenFields(t, 1 / MILLION, true),
        };
      });
    }
    out[name] = entry;
  }
  return out;
};
